// A clarifying question on a dev plan item, raised as a subtask on the task
// board rather than kept in somebody's head.
//
// WHY THIS EXISTS. Luyanda, 16 September 2026: "If he has clarifying questions
// to ask the team, make it a sub task." The reason it matters is that an item
// sitting at not_started because nobody answered a question looks exactly like
// an item sitting at not_started because nobody started it. One of those is
// Leduma's to fix and the other is somebody else's, and the board could not
// tell them apart.
//
// The question goes to named people, plural allowed but discouraged in the
// copy: two owners on a question is two people who can each assume the other is
// answering it.
import { useMemo, useState } from 'react'
import { HelpCircle, Loader2 } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { toast } from '../../lib/shadcn/sonner'
import { cn } from '../../lib/shadcn/utils'
import { writeErrorMessage } from '../../utils/writeError'
import { useAskDevPlanQuestion } from '../../hooks/backend/devplan'
import { useGetTaskPeople } from '../../hooks/backend/tasks'

type Person = { name: string; person_id: string | null; has_login: boolean }

export function AskTheTeamDialog({
  itemRef,
  itemTitle,
  onRaised,
}: {
  itemRef: string
  itemTitle: string
  onRaised: () => void
}) {
  const [open, setOpen] = useState(false)
  const [question, setQuestion] = useState('')
  const [byDate, setByDate] = useState('')
  const [picked, setPicked] = useState<string[]>([])
  const [busy, setBusy] = useState(false)

  const ask = useAskDevPlanQuestion()
  const people = useGetTaskPeople<Person[]>()

  const options = useMemo(
    () => (people.data ?? []).filter((p) => p.person_id !== null),
    [people.data],
  )

  function openIt() {
    setOpen(true)
    setQuestion('')
    setByDate('')
    setPicked([])
    void people.trigger({})
  }

  async function submit() {
    if (question.trim() === '') {
      toast.error('Say what you need to know.')
      return
    }
    if (picked.length === 0) {
      toast.error('Say who has to answer it. A question addressed to nobody is a note to yourself.')
      return
    }
    setBusy(true)
    try {
      await ask.trigger({
        ref: itemRef,
        question: question.trim(),
        askOf: picked,
        byDate: byDate || null,
      })
      toast.success(`Raised as a subtask under ${itemRef} on the task board.`)
      setOpen(false)
      onRaised()
    } catch (e) {
      toast.error(writeErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  if (!open) {
    return (
      <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={openIt}>
        <HelpCircle className="h-3.5 w-3.5" />
        Ask the team
      </Button>
    )
  }

  return (
    <div className="rounded border border-border bg-card p-3">
      <div className="text-xs font-semibold">
        A question on {itemRef}
        <span className="ml-1 font-normal text-muted-foreground">{itemTitle}</span>
      </div>

      <Input
        autoFocus
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        placeholder="What do you need to know before this can move?"
        className="mt-2 h-8 text-xs"
      />

      <div className="mt-2 flex flex-wrap gap-1">
        {people.loading && options.length === 0 && (
          <span className="text-[11px] text-muted-foreground">Loading the team…</span>
        )}
        {options.map((p) => {
          const on = picked.includes(p.name)
          return (
            <button
              key={p.name}
              type="button"
              onClick={() =>
                setPicked((cur) => (on ? cur.filter((n) => n !== p.name) : [...cur, p.name]))
              }
              className={cn(
                'rounded-full border px-2 py-0.5 text-[11px]',
                on
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border text-muted-foreground hover:text-foreground',
              )}
            >
              {p.name}
            </button>
          )
        })}
      </div>

      <div className="mt-2 flex items-center gap-2">
        <label className="text-[11px] text-muted-foreground">Answer by</label>
        <input
          type="date"
          value={byDate}
          onChange={(e) => setByDate(e.target.value)}
          className="rounded border border-input bg-background px-1.5 py-1 text-xs"
        />
        <span className="text-[11px] text-muted-foreground">
          Optional. Without one it cannot be overdue.
        </span>
      </div>

      <div className="mt-3 flex gap-2">
        <Button size="sm" className="h-7 gap-1.5" disabled={busy} onClick={() => void submit()}>
          {busy && <Loader2 className="h-3 w-3 animate-spin" />}
          Raise it
        </Button>
        <Button size="sm" variant="ghost" className="h-7" disabled={busy} onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </div>
  )
}
